/**
 * AddCustomCheckModal.js
 *
 * Modal for adding a custom pre-departure check
 */

import React, { useState } from 'react';
import { X } from 'lucide-react';
import { CHECK_CATEGORIES } from '../../../../config/operations/preDepartureChecksLibrary';

const AddCustomCheckModal = ({ isOpen, onClose, onAdd }) => {
  const [text, setText] = useState('');
  const [category, setCategory] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!text.trim()) {
      setError('Please enter check text');
      return;
    }

    if (text.trim().length < 3) {
      setError('Check text must be at least 3 characters');
      return;
    }

    onAdd(text.trim(), category || null);
    setText('');
    setCategory('');
    setError('');
  };

  const handleClose = () => {
    setText('');
    setCategory('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-25" onClick={handleClose} />

      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Add Custom Check</h3>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-500">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Check Text *
              </label>
              <input
                type="text"
                value={text}
                onChange={(e) => { setText(e.target.value); setError(''); }}
                placeholder="e.g., Check tender fuel level"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg
                  focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                autoFocus
              />
              {error && (
                <p className="mt-1 text-sm text-red-600">{error}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category (optional)
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg
                  focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              >
                <option value="">No category</option>
                {CHECK_CATEGORIES.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Organise your check under a category
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white
                  border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white
                  bg-cyan-600 rounded-lg hover:bg-cyan-700"
              >
                Add Check
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddCustomCheckModal;
